import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveToSavedIdeas } from '../services/savedIdeasService';

const AddIdea: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState<string|null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setLoading(true);
    try {
      await saveToSavedIdeas({
        title,
        description,
        potentialMarket: targetMarket,
        industry,
        skillsUtilized: [],
        createdAt: new Date().toISOString(),
      });
      try {
        window.dispatchEvent(new Event('bp-data-changed'));
      } catch {
      }
      navigate('/saved-ideas');
    } catch (err) {
      setError('Failed to save idea. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 relative overflow-hidden min-h-screen">
      {/* Animated background with mesh pattern and blobs */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMDAnIGhlaWdodD0nMTAwJyB2aWV3Qm94PScwIDAgMTAwIDEwMCc+PHJlY3QgZmlsbD0nbm9uZScgc3Ryb2tlPScjZTBlMGUwJyBzdHJva2Utd2lkdGg9JzAuNycgd2lkdGg9JzEwMCcgaGVpZ2h0PScxMDAnIHN0cm9rZS1vcGFjaXR5PScwLjInLz48cmVjdCBmaWxsPSdub25lJyBzdHJva2U9JyNlMGUwZTAnIHN0cm9rZS13aWR0aD0nMC43JyB4PScyNScgeT0nMjUnIHdpZHRoPSc1MCcgaGVpZ2h0PSc1MCcgc3Ryb2tlLW9wYWNpdHk9JzAuMicvPjxyZWN0IGZpbGw9J25vbmUnIHN0cm9rZT0nI2UwZTBlMCcgc3Ryb2tlLXdpZHRoPScwLjknIHg9JzUwJyB5PScwJyB3aWR0aD0nMScgaGVpZ2h0PScxMDAnIHN0cm9rZS1vcGFjaXR5PScwLjInLz48cmVjdCBmaWxsPSdub25lJyBzdHJva2U9JyNlMGUwZTAnIHN0cm9rZS13aWR0aD0nMC45JyB4PScwJyB5PSc1MCcgd2lkdGg9JzEwMCcgaGVpZ2h0PScxJyBzdHJva2Utb3BhY2l0eT0nMC4yJy8+PC9zdmc+JyldIbg-[size:50px_50px]"></div>
      </div>

      {/* Animated blobs */}
      <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob"></div>
      <div className="absolute top-40 right-12 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-12 left-1/2 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-4000"></div>
      
      <div className="container relative z-10 mx-auto px-6 flex flex-col items-center">
        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-8">Save Your Own Business Idea</h1>
        
        <div className="relative overflow-hidden w-full max-w-lg">
          <form onSubmit={handleSubmit} className="relative z-10 bg-white/40 backdrop-blur-lg border border-white/40 shadow-lg rounded-xl p-8 w-full space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Title <span className="text-blue-600">*</span></label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                required 
                className="w-full bg-white/70 backdrop-blur-sm border border-white/40 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300" 
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Description <span className="text-blue-600">*</span></label>
              <textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                required 
                className="w-full bg-white/70 backdrop-blur-sm border border-white/40 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300" 
                rows={4} 
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Target Market</label>
              <input 
                type="text" 
                value={targetMarket} 
                onChange={e => setTargetMarket(e.target.value)} 
                className="w-full bg-white/70 backdrop-blur-sm border border-white/40 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300" 
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Industry</label>
              <input 
                type="text" 
                value={industry} 
                onChange={e => setIndustry(e.target.value)} 
                className="w-full bg-white/70 backdrop-blur-sm border border-white/40 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300" 
              />
            </div>
            
            {error && (
              <div className="relative overflow-hidden rounded-lg p-3 bg-red-50/70 backdrop-blur-sm border border-red-200">
                <p className="text-red-600 font-medium">{error}</p>
              </div>
            )}
            
            <button 
              type="submit" 
              className="w-full relative overflow-hidden group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-semibold text-lg shadow-md transform transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:transition-none" 
              disabled={loading}
            >
              <span className="absolute top-0 left-0 w-full h-full bg-white/20 transform -skew-x-12 -translate-x-full transition-transform duration-700 ease-in-out group-hover:translate-x-full"></span>
              <span className="relative z-10">{loading ? 'Saving...' : 'Save Idea'}</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AddIdea;
